import {track} from '@getmoss/moss-analytics'
import type {LinkField} from '@prismicio/client'
import * as prismic from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import type {ReactNode} from 'react'
import {Fragment, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {ButtonV2} from 'src/common/ButtonV2'
import {getDeviceInfo} from 'src/common/helpers/deviceInfo'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {Icon} from 'src/common/Icon'
import {DOWNLOAD_REPOSITORY_ANCHOR} from '../constants'
import {TabSwitcher} from './TabSwitcher'
import {useScrollRevealScale} from './useScrollRevealScale'

type OsTab = 'macos' | 'windows'
type ActiveTab = OsTab | 'ai-agent'

const VIDEO_GUIDE_LABELS: Record<ActiveTab, string> = {
  macos: 'macOS',
  windows: 'Windows',
  'ai-agent': 'AI prompt',
}

const getSteps = (
  tab: OsTab,
  repositoryUrl: string,
  onRepositoryLinkClick: () => void,
  onWatchVideoClick?: () => void,
): ReactNode[] => [
  <span key="install-guide">
    Watch the{' '}
    {onWatchVideoClick ? (
      <button type="button" onClick={onWatchVideoClick} className="text-green-neon underline">
        {VIDEO_GUIDE_LABELS[tab]} installation video
      </button>
    ) : (
      <>{VIDEO_GUIDE_LABELS[tab]} installation video</>
    )}
  </span>,
  <span key="repository">
    Go to the{' '}
    <a
      href={repositoryUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-green-neon underline"
      onClick={onRepositoryLinkClick}
    >
      GitHub repository
    </a>
  </span>,
]

const DEMO_VIDEOS: Record<ActiveTab, {src: string; label: string}> = {
  macos: {
    src: 'https://mosswebsite.cdn.prismic.io/mosswebsite/BEprub6K_jlYICxv_MossAI-mac.mp4',
    label: 'MossAI-macfinal.mp4',
  },
  windows: {
    src: 'https://mosswebsite.cdn.prismic.io/mosswebsite/nv-ycm7L0JHpWoBT_MossAI-win.mp4',
    label: 'MossAI-win.mp4',
  },
  'ai-agent': {
    src: 'https://mosswebsite.cdn.prismic.io/mosswebsite/Bcc9sn7S9XFmCM3l_MossAI-promptfinal.mp4',
    label: 'MossAI-promptfinal.mp4',
  },
}

const getRepositoryName = (url: string): string => {
  const match = /\/([^/]+?)(\.git)?\/?$/.exec(url)
  return match ? match[1] : url
}

type InstallGuideStep = {
  text: ReactNode
  commands?: string[]
  links?: {href: string; label?: string}[]
  note?: string
  timeSeconds?: number
  sectionTitle?: string
}

const formatTimecode = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const getInstallGuideSteps = (tab: OsTab, repositoryUrl: string): InstallGuideStep[] => {
  if (tab === 'windows') {
    return [
      {text: "Create a new folder (name doesn't matter)", timeSeconds: 0, sectionTitle: 'One-time setup:'},
      {
        text: 'Install Git (skip this step if Git is already installed):',
        commands: ['winget install --id Git.Git -e'],
        timeSeconds: 4,
      },
      {
        text: 'Copy a link to repository:',
        commands: [`git clone ${repositoryUrl}`],
        timeSeconds: 22,
      },
      {
        text: 'Install Node.js from official website:',
        links: [{href: 'https://nodejs.org/en/download'}],
        timeSeconds: 35,
      },
      {
        text: 'Start the app:',
        commands: ['npm start'],
        note: "Copy the address from your terminal and paste it into your browser's address bar.",
        timeSeconds: 83,
        sectionTitle: 'Run it anytime like this:',
      },
      {
        text: 'Update the app (optional step):',
        commands: ['git pull'],
        note: 'This downloads the latest version, in case it was updated since you installed it.',
      },
    ]
  }

  return [
    {text: "Create a new folder (name doesn't matter)", timeSeconds: 0, sectionTitle: 'One-time setup:'},
    {
      text: 'Install Git (skip this step if Git is already installed):',
      commands: ['xcode-select --install'],
      timeSeconds: 4,
    },
    {
      text: 'Copy a link to repository:',
      commands: [`git clone ${repositoryUrl}`],
      timeSeconds: 17,
    },
    {
      text: 'Install Node.js from official website:',
      links: [{href: 'https://nodejs.org/en/download'}],
      timeSeconds: 32,
    },
    {
      text: 'Start the app:',
      commands: ['npm start'],
      note: "Copy the address from your terminal and paste it into your browser's address bar.",
      timeSeconds: 57,
      sectionTitle: 'Run it anytime like this:',
    },
    {
      text: 'Update the app (optional step):',
      commands: ['git pull'],
      note: 'This downloads the latest version, in case it was updated since you installed it.',
    },
  ]
}

const getAgentPrompt = (
  repositoryUrl: string,
  repositoryName: string,
): string => `Clone and set up the ${repositoryName} tool for me. Act as my setup assistant using your file system and terminal access:
1. First check whether the current folder (or a \`${repositoryName}\` subfolder) already contains this tool (look for server.mjs and index.html). If it's already there, run \`git pull\` inside that folder to get the latest changes, then skip straight to step 3.
2. Otherwise, clone the repo:
   git clone ${repositoryUrl}
   then work from inside that folder, and check whether Git, nvm, and the Node.js version this repo's README requires are installed, installing or switching to whatever is missing.
3. Ask me whether I want to run it in production mode (connecting my real accounts with API keys) or demo mode (mocked data, so I can just see how it looks without any API keys). Wait for my answer before continuing.
4. Double check that everything needed is set up correctly, and then run \`npm start\` for production mode, or \`npm run demo\` for demo mode, based on my answer.
5. Watch the terminal output for errors. If something fails, diagnose the root cause, explain it to me in plain language, and either fix it or tell me exactly what to run.
6. Tell me the URL to open in my browser once the server is running.
7. Later, if I ask you to stop or restart the tool, do that for me.

Important: never read, print, or send the contents of my .env file, and never share any API keys or company data outside this terminal session.`

const PromptBlock = ({prompt, truncated}: {prompt: string; truncated?: boolean}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard
      .writeText(prompt)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {})
  }

  return (
    <div className="relative overflow-hidden rounded bg-[#ffffff1f] p-3">
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy prompt to clipboard'}
        className="absolute right-3 top-3 z-10 shrink-0 text-grey-200/90 transition-colors duration-150 hover:text-moss-white"
      >
        <Icon name={copied ? 'check' : 'copy'} className="size-3.5" />
      </button>
      <pre
        className={cn(
          'font-mono whitespace-pre-wrap break-words pr-8 text-[11px] leading-5 text-moss-white',
          truncated && 'h-[230px] overflow-hidden',
        )}
      >
        {prompt}
      </pre>
      {truncated && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-green-dark to-transparent" />
      )}
    </div>
  )
}

const CodeLine = ({children}: {children: string}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard
      .writeText(children)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {})
  }

  return (
    <div className="flex items-center justify-between gap-2 rounded bg-[#ffffff1f] px-2 py-1">
      <code className="break-all text-[11px] text-moss-white">{children}</code>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy to clipboard'}
        className="shrink-0 text-grey-200/90 transition-colors duration-150 hover:text-moss-white"
      >
        <Icon name={copied ? 'check' : 'copy'} className="size-3" />
      </button>
    </div>
  )
}

const LinkLine = ({href, label}: {href: string; label?: string}) => {
  const [copied, setCopied] = useState(false)
  const displayText = label ?? href

  const handleCopy = () => {
    navigator.clipboard
      .writeText(displayText)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {})
  }

  return (
    <div className="flex items-center justify-between gap-2 rounded bg-[#ffffff1f] px-2 py-1">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-mono break-all text-[11px] text-green-neon underline"
      >
        {displayText}
      </a>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy to clipboard'}
        className="shrink-0 text-grey-200/90 transition-colors duration-150 hover:text-moss-white"
      >
        <Icon name={copied ? 'check' : 'copy'} className="size-3" />
      </button>
    </div>
  )
}

const InstallGuideList = ({steps, onSeek}: {steps: InstallGuideStep[]; onSeek: (seconds: number) => void}) => (
  <>
    {steps.map((step, i) => (
      <Fragment key={i}>
        {step.sectionTitle && (
          <li className={cn('text-[13px] font-semibold text-moss-white', i > 0 && 'border-t border-grey-200/20 pt-3')}>
            {step.sectionTitle}
          </li>
        )}
        <li className="flex flex-col gap-1.5 text-[12px] font-normal leading-5 text-grey-100">
          <span>
            <span className="font-mono text-grey-200/90">{String.fromCharCode(97 + i)}.</span> {step.text}
            {step.timeSeconds !== undefined && (
              <button
                type="button"
                onClick={() => onSeek(step.timeSeconds ?? 0)}
                className="font-mono ml-1.5 text-[11px] text-green-neon underline decoration-dotted underline-offset-2 hover:text-moss-white"
              >
                {formatTimecode(step.timeSeconds)}
              </button>
            )}
          </span>
          {step.commands?.map((command) => (
            <CodeLine key={command}>{command}</CodeLine>
          ))}
          {step.note && <span className="text-grey-200/90">{step.note}</span>}
          {step.links?.map((link) => (
            <LinkLine key={link.href} href={link.href} label={link.label} />
          ))}
        </li>
      </Fragment>
    ))}
  </>
)

const InstallGuide = ({
  steps,
  onSeek,
  intro,
  hideBorder,
}: {
  steps: InstallGuideStep[]
  onSeek: (seconds: number) => void
  intro?: string
  hideBorder?: boolean
}) => (
  <div className="mt-2 flex flex-col gap-3">
    {intro && <p className="text-[12px] font-normal leading-5 text-grey-200/90">{intro}</p>}
    <ol className={cn('flex flex-col gap-3', hideBorder ? 'pl-0' : 'border-l border-green-deep pl-4')}>
      <InstallGuideList steps={steps} onSeek={onSeek} />
    </ol>
  </div>
)

// Keeps each DownloadRepositorySection instance's motion.div layoutId unique so framer-motion
// doesn't treat multiple sections on the same page as the same shared element
let downloadRepositoryInstanceCounter = 0

export const DownloadRepositorySection = ({githubRepositoryLink}: {githubRepositoryLink: LinkField}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('ai-agent')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const domainCountry = useGetDomainCountry()
  const instanceIdRef = useRef<number>()
  const videoRef = useRef<HTMLVideoElement>(null)
  const {ref: chromeRef, scale: scrollScale} = useScrollRevealScale()

  if (instanceIdRef.current === undefined) {
    instanceIdRef.current = ++downloadRepositoryInstanceCounter
  }

  const windowLayoutId = `download-repository-window-${instanceIdRef.current}`

  // Mount on client side so the fullscreen portal target is available
  useEffect(() => {
    setMounted(true)
  }, [])

  // Pre-select the tab matching the visitor's OS so they see relevant steps immediately
  useEffect(() => {
    const {os} = getDeviceInfo()

    if (os === 'Windows') {
      setActiveTab('windows')
    } else if (os === 'MacOS') {
      setActiveTab('macos')
    }
  }, [])

  // The video element remounts per tab (key={activeTab}), so it always starts paused
  useEffect(() => {
    setIsVideoPlaying(false)
  }, [activeTab])

  // Disable scrolling while fullscreen. Locking both html and body (rather than body alone)
  // covers browsers where the scrolling container is the root element. Avoid touching
  // `position`/`top` here — that shifts the body's layout origin and breaks the shared
  // layoutId transition framer-motion uses to animate between the compact and fullscreen views.
  useEffect(() => {
    if (!isFullscreen) {
      return undefined
    }

    const originalBodyOverflow = document.body.style.overflow
    const originalHtmlOverflow = document.documentElement.style.overflow

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalBodyOverflow
      document.documentElement.style.overflow = originalHtmlOverflow
    }
  }, [isFullscreen])

  // Exit fullscreen on Escape key
  useEffect(() => {
    if (!isFullscreen) {
      return undefined
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => document.removeEventListener('keydown', handleEscape)
  }, [isFullscreen])

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev)
  }

  const seekVideoTo = (seconds: number) => {
    const video = videoRef.current

    if (!video) {
      return
    }

    video.currentTime = seconds
    video.play().catch(() => {})
  }

  const repositoryUrl = isFilled.link(githubRepositoryLink) ? prismic.asLink(githubRepositoryLink) : null
  const repositoryName = repositoryUrl ? getRepositoryName(repositoryUrl) : ''

  const switchTab = (tab: ActiveTab) => {
    setActiveTab(tab)

    track({
      event: 'interaction',
      interaction: {
        type: 'click',
        element: 'download-repository-tab',
        elementText: tab,
        location: 'blog-post-download-repository-section',
      },
      page: {
        domainCountry,
        type: 'blog-post',
      },
    })
  }

  const trackRepositoryLinkClick = () => {
    track({
      event: 'interaction',
      interaction: {
        type: 'click',
        element: 'download-repository-link',
        elementText: 'GitHub repository',
        location: 'blog-post-download-repository-section',
        detail: {tab: activeTab, repository: `/${repositoryName}`},
      },
      page: {
        domainCountry,
        type: 'blog-post',
      },
    })
  }

  const trackRepositoryButtonClick = () => {
    track({
      event: 'interaction',
      interaction: {
        type: 'click',
        element: 'download-repository-button',
        elementText: 'View on GitHub',
        location: 'blog-post-download-repository-section',
        detail: {tab: activeTab, repository: `/${repositoryName}`},
      },
      page: {
        domainCountry,
        type: 'blog-post',
      },
    })
  }

  const guideLabel = 'View full video guide'

  const trackViewGuideClick = (isFullscreenView: boolean) => {
    track({
      event: 'interaction',
      interaction: {
        type: 'click',
        element: 'download-repository-guide-button',
        elementText: isFullscreenView ? 'Close' : guideLabel,
        location: 'blog-post-download-repository-section',
        detail: activeTab,
      },
      page: {
        domainCountry,
        type: 'blog-post',
      },
    })
  }

  const openVideoGuideStep = () => {
    track({
      event: 'interaction',
      interaction: {
        type: 'click',
        element: 'download-repository-video-step-link',
        elementText: `${VIDEO_GUIDE_LABELS[activeTab]} installation video`,
        location: 'blog-post-download-repository-section',
        detail: activeTab,
      },
      page: {
        domainCountry,
        type: 'blog-post',
      },
    })

    toggleFullscreen()
  }

  if (!repositoryUrl) {
    return null
  }

  const tabSwitcher = (
    <TabSwitcher
      className="sm:w-[360px]"
      activeValue={activeTab}
      onChange={switchTab}
      options={[
        {value: 'macos', label: 'macOS', icon: 'apple-logo', iconClassName: 'size-4'},
        {value: 'windows', label: 'Windows', icon: 'windows-logo', iconClassName: 'size-3'},
        {value: 'ai-agent', label: 'AI Prompt', icon: 'sparkle', iconClassName: 'size-3.5'},
      ]}
    />
  )

  const guideButton = (isFullscreenView: boolean) => (
    <ButtonV2
      variant="primary"
      className="text-button-xs !h-8 self-start"
      onClick={() => {
        trackViewGuideClick(isFullscreenView)
        toggleFullscreen()
      }}
      trackingData={{
        mktgId: 'blog-post-view-guide-button',
        dataType: 'blog-post-view-guide-button',
        location: 'blog-post-download-repository-section',
        pageName: 'blog-post',
      }}
    >
      <span className="flex items-center gap-1.5">
        {!isFullscreenView && <Icon name="right-arrow" className="size-3" />}
        {isFullscreenView ? 'Close' : guideLabel}
      </span>
    </ButtonV2>
  )

  const repositoryLinkButton = (isFullscreenView: boolean) => (
    <a
      href={repositoryUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={trackRepositoryButtonClick}
      className={cn(
        'flex h-8 shrink-0 animate-attention-glow items-center gap-1.5 whitespace-nowrap rounded-[4px] border border-green-light bg-[#ffffff0a] px-4 text-[12px] font-medium transition duration-300 hover:bg-[#ffffff1a] hover:text-moss-white',
        isFullscreenView ? 'text-moss-white' : 'text-grey-100',
      )}
    >
      View on GitHub
      <Icon name="right-arrow" className="size-3" />
    </a>
  )

  const windowChrome = (isFullscreenView: boolean) => (
    <motion.div
      ref={isFullscreenView ? undefined : chromeRef}
      layoutId={windowLayoutId}
      layoutDependency={isFullscreenView ? undefined : isFullscreen}
      transition={{type: 'spring', damping: 32, stiffness: 320}}
      style={isFullscreenView ? undefined : {scale: scrollScale}}
      className={cn(
        'group/window flex flex-col overflow-hidden border border-green-deep bg-green-dark',
        isFullscreenView ? 'fixed inset-0 z-50 h-[100dvh] w-screen' : 'rounded-lg',
      )}
    >
      <div
        className={cn(
          'relative flex h-[41px] shrink-0 items-center gap-2 border-b border-green-deep px-4',
          !isFullscreenView && 'rounded-t-lg',
        )}
      >
        {/* Traffic-light window controls, disabled for now in favor of the explicit fullscreen button below
        {isFullscreenView ? (
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label="Close fullscreen"
            className="relative flex size-3 items-center justify-center rounded-full bg-[#FF5F57]"
          >
            <svg
              viewBox="0 0 6 6"
              className="absolute inset-0 m-auto size-1.5 opacity-0 transition-opacity duration-150 group-hover/window:opacity-100"
            >
              <path
                d="M1.2 1.2L4.8 4.8M4.8 1.2L1.2 4.8"
                stroke="#7A2620"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : (
          <span className="size-3 rounded-full bg-[#FF5F57]" />
        )}
        <span className="size-3 rounded-full bg-[#FFBD2E]" />
        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label={isFullscreenView ? 'Exit fullscreen' : 'View fullscreen'}
          className="flex size-3 items-center justify-center rounded-full bg-[#28C840] max-lg:pointer-events-none"
        >
          <svg
            viewBox="0 0 6 6"
            className="size-1.5 opacity-0 transition-opacity duration-150 group-hover/window:opacity-100 max-lg:!opacity-0"
          >
            <path
              d="M3.4 1H5V2.6M2.6 5H1V3.4"
              stroke="#0C3A22"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        */}
        <span className="pointer-events-none absolute inset-x-0 text-center text-[10px] font-medium tracking-widest text-grey-200">
          {repositoryName}
        </span>
        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label={isFullscreenView ? 'Exit fullscreen' : 'View fullscreen'}
          className="ml-auto flex size-8 items-center justify-center rounded-lg text-grey-200/70 transition-colors duration-300 hover:bg-[#ffffff1f] group-hover/window:animate-none group-hover/window:text-moss-white motion-safe:animate-icon-flicker"
        >
          <Icon name="fullscreen" className="size-4" />
        </button>
      </div>
      <div
        className={cn(
          'flex w-full gap-5 overflow-y-auto p-4',
          isFullscreenView
            ? 'flex-1 flex-col items-center gap-8 px-4 pb-6 pt-8 lg:flex-row lg:gap-20 lg:px-16 lg:pb-12 lg:pt-32'
            : 'flex-col',
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-5',
            isFullscreenView &&
              cn(
                'order-2 w-full shrink-0 gap-8 lg:order-1 lg:self-start',
                activeTab === 'ai-agent' ? 'lg:max-w-2xl' : 'lg:max-w-[475px]',
              ),
          )}
        >
          <div className="flex flex-col gap-4">
            {tabSwitcher}
            <div className="flex flex-col gap-2">
              {!isFullscreenView && <h3 className="text-[15px] font-semibold text-moss-white">Getting started</h3>}
              {activeTab === 'ai-agent' && !isFullscreenView && (
                <div className="text-[13px] font-normal leading-5 text-moss-white">
                  Watch the{' '}
                  <button type="button" onClick={openVideoGuideStep} className="text-green-neon underline">
                    {VIDEO_GUIDE_LABELS[activeTab]} installation video
                  </button>
                </div>
              )}
              {activeTab === 'ai-agent' && (
                <div
                  className={cn('flex flex-col gap-3', !isFullscreenView && 'max-h-[200px] min-h-0 overflow-hidden')}
                >
                  <p className="shrink-0 text-[13px] font-normal leading-5 text-grey-100">
                    Works with Claude Code, OpenAI's Codex, and other terminal-based AI coding agents. Copy this prompt
                    and paste it into your agent - it will clone the repo, install dependencies, and start the app for
                    you.
                  </p>
                  <PromptBlock prompt={getAgentPrompt(repositoryUrl, repositoryName)} truncated={!isFullscreenView} />
                </div>
              )}
              {isFullscreenView && activeTab !== 'ai-agent' && (
                <div className="flex flex-col">
                  <div className="text-[13px] font-normal leading-5 text-moss-white">
                    {getSteps(activeTab, repositoryUrl, trackRepositoryLinkClick, undefined)[0]}
                  </div>
                  <InstallGuide
                    steps={getInstallGuideSteps(activeTab, repositoryUrl)}
                    onSeek={seekVideoTo}
                    intro="A terminal lets you control your computer by typing commands instead of clicking - it's how developers install and run tools. We'll use it for the next few steps to get everything set up."
                    hideBorder
                  />
                </div>
              )}
              {activeTab !== 'ai-agent' && !isFullscreenView && (
                <ol className="flex flex-col gap-2">
                  {getSteps(activeTab, repositoryUrl, trackRepositoryLinkClick, openVideoGuideStep).map((step, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13px] font-normal leading-5 text-moss-white">
                      <span className="font-mono shrink-0 text-[12px] font-medium text-grey-200/90 [font-variant-numeric:slashed-zero]">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {guideButton(isFullscreenView)}
            {repositoryLinkButton(isFullscreenView)}
          </div>
        </div>
        {isFullscreenView && (
          <div className="order-1 flex size-full min-w-0 flex-1 items-start justify-center lg:order-2 lg:w-auto">
            <div className="flex size-full max-h-full flex-col items-end gap-2 lg:h-[90%]">
              <div className="relative aspect-[16/11] max-h-full w-full max-w-full flex-1 overflow-hidden rounded-2xl bg-gradient-to-br from-[#ffffff0f] to-transparent shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] outline outline-1 outline-[#ffffff1a]">
                <span className="font-mono absolute left-4 top-4 z-10 rounded bg-black/40 px-2 py-1 text-[11px] text-grey-200 backdrop-blur-sm">
                  {DEMO_VIDEOS[activeTab].label}
                </span>
                <video
                  ref={videoRef}
                  key={activeTab}
                  src={DEMO_VIDEOS[activeTab].src}
                  loop
                  playsInline
                  controls
                  preload="auto"
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  className="size-full object-contain"
                />
                {!isVideoPlaying && (
                  <button
                    type="button"
                    onClick={() => videoRef.current?.play()}
                    aria-label="Play video"
                    className="absolute inset-0 z-10 flex items-center justify-center"
                  >
                    <div className="flex size-20 -translate-y-3 items-center justify-center rounded-full bg-green-neon backdrop-blur-[9.8px] transition-transform hover:scale-110">
                      <Icon name="play" className="h-[34px] w-[34px] text-moss-black" />
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )

  return (
    <div id={DOWNLOAD_REPOSITORY_ANCHOR} className="flex scroll-mt-16 flex-col gap-4 pt-4 lg:pt-6">
      {!isFullscreen && windowChrome(false)}
      {mounted && createPortal(<AnimatePresence>{isFullscreen && windowChrome(true)}</AnimatePresence>, document.body)}
    </div>
  )
}
